import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Sanitize sheet name for Excel (max 31 chars, no \ / * ? : [ ]).
 */
function safeSheetName(name) {
  if (!name || typeof name !== 'string') return 'Silo';
  const sanitized = name.replace(/[\\/*?:[\]]/g, ' ').trim();
  return sanitized.length > 31 ? sanitized.substring(0, 31) : sanitized || 'Silo';
}

/**
 * Generate Excel for consolidated range: one tab per silo with interval values (entradas/saídas kg and sacas)
 * and below the table a summary line with total entradas and total saídas in kg and sacas.
 * @param {Object} rangeDataWithSacas - { intervals8h, siloOrder, intervalsPerSilo }
 * @param {Object} locationInfo - { farmName, barnName }
 * @param {string} startDate
 * @param {string} endDate
 * @param {number} sacaWeight
 * @param {number} hours - interval hours
 */
export const generateConsolidated8hRangeExcel = async (
  rangeDataWithSacas,
  locationInfo,
  startDate,
  endDate,
  sacaWeight,
  hours
) => {
  const siloOrder = rangeDataWithSacas?.siloOrder || [];
  const intervalsPerSilo = rangeDataWithSacas?.intervalsPerSilo || [];

  if (!siloOrder.length || !intervalsPerSilo.length) {
    console.error('No hay datos para exportar');
    return;
  }

  const startStr = startDate ? format(new Date(startDate), 'dd/MM/yyyy', { locale: ptBR }) : '';
  const endStr = endDate ? format(new Date(endDate), 'dd/MM/yyyy', { locale: ptBR }) : '';
  const fileName = `Consolidado ${hours}h - ${locationInfo.farmName} - ${locationInfo.barnName} - ${startStr} - ${endStr}.xlsx`;

  const workbook = new ExcelJS.Workbook();

  siloOrder.forEach(({ id: siloId, name: siloName }) => {
    const siloData = intervalsPerSilo.find(s => String(s.siloId) === String(siloId));
    const intervals = (siloData && siloData.intervals) || [];

    const sheetName = safeSheetName(siloName);
    const worksheet = workbook.addWorksheet(sheetName, { pageSetup: { fitToPage: true } });

    worksheet.getCell('B1').value = `Consolidado Entradas / Saídas - ${siloName}`;
    worksheet.getCell('B1').font = { bold: true, size: 14 };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };
    worksheet.getCell('B2').value = `${locationInfo.farmName} - ${locationInfo.barnName}`;
    worksheet.getCell('B2').font = { bold: true };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };
    worksheet.getCell('B3').value = `${startStr} - ${endStr} (intervalo ${hours}h)`;
    worksheet.getCell('B3').font = { italic: true };
    worksheet.getCell('B3').alignment = { horizontal: 'center' };
    worksheet.getColumn('B').width = 40;

    let totalEntradasKg = 0;
    let totalSaidasKg = 0;
    let totalEntradasSacas = 0;
    let totalSaidasSacas = 0;

    intervals.forEach((interval) => {
      // Para o Excel, usamos sempre os kg "reais" (entradasKgReal/saidasKgReal),
      // caindo para entradasKg/saidasKg apenas se não houver os reais.
      const entradasFonteKg = interval.entradasKgReal != null ? interval.entradasKgReal : interval.entradasKg;
      const saidasFonteKg = interval.saidasKgReal != null ? interval.saidasKgReal : interval.saidasKg;
      totalEntradasKg += Number(entradasFonteKg || 0);
      totalSaidasKg += Number(saidasFonteKg || 0);
      totalEntradasSacas += Number(interval.entradasSacas || 0);
      totalSaidasSacas += Number(interval.saidasSacas || 0);
    });

    const summaryCell = worksheet.getCell('A4');
    summaryCell.value = `Resumo: Total Entradas: ${totalEntradasKg.toFixed(2)} kg (${totalEntradasSacas.toFixed(2)} sacas)  |  Total Saídas: ${totalSaidasKg.toFixed(2)} kg (${totalSaidasSacas.toFixed(2)} sacas)`;
    summaryCell.font = { bold: true };

    const headerRow = worksheet.getRow(6);
    headerRow.values = [
      'Período',
      'Entradas (kg)',
      'Saídas (kg)',
      'Entradas (sacas)',
      'Saídas (sacas)'
    ];
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };

    intervals.forEach((interval, index) => {
      const startDt = interval.start ? parseISO(interval.start) : null;
      const endDt = interval.end ? parseISO(interval.end) : null;
      const start = startDt ? format(startDt, 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '';
      // Ajustamos o fim para 1 minuto antes (ex.: 00:00 - 23:59 em intervalo de 24h)
      const endAdj = endDt ? new Date(endDt.getTime() - 60 * 1000) : null;
      const end = endAdj ? format(endAdj, 'HH:mm', { locale: ptBR }) : '';
      const periodLabel = `${start} - ${end}`;
      const entradasFonteKg = interval.entradasKgReal != null ? interval.entradasKgReal : interval.entradasKg;
      const saidasFonteKg = interval.saidasKgReal != null ? interval.saidasKgReal : interval.saidasKg;
      const entradasKg = Number(entradasFonteKg || 0);
      const saidasKg = Number(saidasFonteKg || 0);
      const entradasSacas = Number(interval.entradasSacas || 0);
      const saidasSacas = Number(interval.saidasSacas || 0);

      const row = worksheet.getRow(7 + index);
      row.values = [
        periodLabel,
        entradasKg.toFixed(2),
        saidasKg.toFixed(2),
        entradasSacas.toFixed(2),
        saidasSacas.toFixed(2)
      ];
    });

    worksheet.getColumn(1).width = 22;
    worksheet.getColumn(2).width = 14;
    worksheet.getColumn(3).width = 14;
    worksheet.getColumn(4).width = 16;
    worksheet.getColumn(5).width = 16;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
};
